/**
 * WallSnobs — modal controller
 *
 * Open, close and submit for the quote / custom / material / availability
 * modals in snippets/ws-modals.liquid.
 *
 * This used to live inside ws-product-ctas.js, behind an early return on
 * [data-product-ctas] — an element only the old product sections output. The
 * modals themselves render on every page from the layout, so on any other
 * template they were present in the DOM and impossible to open. Splitting the
 * controller out fixes that: the modals now work anywhere they are rendered.
 *
 * Triggers are plain data attributes, so a link can carry a real href as a
 * fallback and still open a modal when scripting is available:
 *
 *   <a href="/pages/custom" data-action="request-custom">Start a custom request</a>
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea';

  /**
   * Current selection. The new PDP owns this in its shared state layer; the
   * older templates express it in the DOM. Prefer the state layer, fall back
   * to reading the page.
   */
  function selection() {
    if (window.WSPdp && window.WSPdp.state && window.WSPdp.state.colorway) {
      return {
        colorway: window.WSPdp.state.colorway || '',
        material: window.WSPdp.state.material || ''
      };
    }
    var radio = document.querySelector('variant-selects input[type="radio"]:checked');
    var card = document.querySelector('.ws-substrate-card.is-active');
    return {
      colorway: radio ? radio.value : '',
      material: card ? card.dataset.substrate : ''
    };
  }

  function openModal(modalId) {
    var modal = document.getElementById(modalId);
    if (!modal) return;

    var sel = selection();

    modal.querySelectorAll('[data-fill="colorway"]').forEach(function (el) {
      el.textContent = sel.colorway || '—';
    });
    modal.querySelectorAll('[data-fill="substrate"]').forEach(function (el) {
      el.textContent = sel.material || '—';
    });

    /* Mural wall size, published by ws-mural.js on the mural template. Absent
       on every other product, and the row stays hidden rather than showing an
       empty "Wall size —" on a roll request. The hidden input is what actually
       travels with the submission. */
    var mural = (window.WSMural && window.WSMural.summary) || '';
    modal.querySelectorAll('[data-fill="mural-size"]').forEach(function (el) {
      el.textContent = mural || '—';
    });
    modal.querySelectorAll('[data-fill-row="mural-size"]').forEach(function (el) {
      el.hidden = !mural;
    });
    var muralInput = modal.querySelector('#custom-mural-size');
    if (muralInput) muralInput.value = mural;

    // Colorway is editable in the custom modal — someone may open it from a
    // colorway they have no intention of ordering, and the request should
    // record what they actually want, not what happened to be on screen.
    var cwSelect = modal.querySelector('[data-colorway-select]');
    if (cwSelect && sel.colorway) {
      var match = Array.prototype.some.call(cwSelect.options, function (o) {
        return o.value === sel.colorway;
      });
      if (match) cwSelect.value = sel.colorway;
    }

    // Pre-tick the material they are looking at, without limiting them to it.
    var matBoxes = modal.querySelectorAll('[data-material-choice]');
    if (matBoxes.length && sel.material) {
      var anyChecked = Array.prototype.some.call(matBoxes, function (cb) { return cb.checked; });
      if (!anyChecked) {
        matBoxes.forEach(function (cb) {
          if (cb.value === sel.material) cb.checked = true;
        });
      }
    }

    if (modalId === 'ws-modal-availability') {
      [['name', 'availability-name'], ['email', 'availability-email'], ['phone', 'availability-phone']]
        .forEach(function (pair) {
          var hidden = document.getElementById('availability-customer-' + pair[0]);
          var input = document.getElementById(pair[1]);
          if (input && !input.value && hidden && hidden.value) input.value = hidden.value;
        });
    }

    // Don't offer to also quote the material they already chose.
    if (modalId === 'ws-modal-quote') {
      modal.querySelectorAll('[data-also-material]').forEach(function (cb) {
        var label = cb.closest('label');
        if (cb.value === sel.material) {
          if (label) label.style.display = 'none';
          cb.checked = false;
        } else if (label) {
          label.style.display = '';
        }
      });
    }

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    modal.dataset.returnFocus = '1';

    var first = modal.querySelector(FOCUSABLE);
    if (first) setTimeout(function () { first.focus(); }, 100);
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function closeAllModals() {
    document.querySelectorAll('.ws-modal.is-open').forEach(closeModal);
  }

  function prepareSubmission(form) {
    var type = form.dataset.wsRequest;
    var modalId = type === 'quote' ? 'ws-modal-quote'
      : type === 'availability' ? 'ws-modal-availability'
      : 'ws-modal-custom';
    var modal = document.getElementById(modalId);
    if (!modal) return;

    var val = function (sel) {
      var el = modal.querySelector(sel);
      return el ? el.value : '';
    };
    var checkedIn = function (sel) {
      return Array.prototype.map.call(modal.querySelectorAll(sel), function (cb) {
        return cb.value;
      }).join(', ');
    };

    // Native form validation also applies when the visitor presses Enter.
    // Trim before validation so whitespace cannot stand in for contact details.
    var nameInput = form.querySelector('[name="contact[name]"]');
    var emailInput = form.querySelector('[name="contact[email]"]');
    if (!nameInput || !emailInput) return false;
    nameInput.value = nameInput.value.trim();
    emailInput.value = emailInput.value.trim();
    if (!form.reportValidity()) return false;

    var sel = selection();
    var titleEl = modal.querySelector('[data-fill="product-title"]');
    var productTitle = titleEl ? titleEl.textContent.trim() : document.title;
    var productUrl = window.location.href;
    var body;

    if (type === 'availability') {
      body = [
        'AVAILABILITY INQUIRY', '---',
        'From: ' + (val('#availability-name') || val('#availability-customer-name')),
        'Email: ' + (val('#availability-email') || val('#availability-customer-email')),
        val('#availability-phone') || val('#availability-customer-phone')
          ? 'Phone: ' + (val('#availability-phone') || val('#availability-customer-phone')) : '',
        val('#availability-customer-company') ? 'Company: ' + val('#availability-customer-company') : '',
        '---',
        'Pattern: ' + productTitle,
        'Colorway: ' + sel.colorway,
        val('#availability-qty') ? 'Quantity Needed: ' + val('#availability-qty') + ' bolts' : '',
        val('#availability-message') ? 'Message: ' + val('#availability-message') : '',
        '---', 'Product URL: ' + productUrl
      ];
    } else if (type === 'quote') {
      var walls = modal.querySelector('input[name="walls-connected"]:checked');
      body = [
        'QUOTE REQUEST', '---',
        'From: ' + val('#quote-customer-name'),
        'Email: ' + val('#quote-customer-email'),
        val('#quote-customer-phone') ? 'Phone: ' + val('#quote-customer-phone') : '',
        val('#quote-customer-company') ? 'Company: ' + val('#quote-customer-company') : '',
        val('#quote-customer-tags') ? 'Account type: ' + val('#quote-customer-tags') : '',
        '---',
        'Pattern: ' + productTitle,
        'Colorway: ' + sel.colorway,
        'Primary Material: ' + sel.material,
        checkedIn('[data-also-material]:checked') ? 'Also quote: ' + checkedIn('[data-also-material]:checked') : '',
        'Project Name: ' + val('#quote-project-name'),
        'Sq Ft: ' + val('#quote-sqft'),
        val('#quote-wall-width') && val('#quote-wall-height')
          ? 'Wall dimensions: ' + val('#quote-wall-width') + '" W x ' + val('#quote-wall-height') + '" H' : '',
        walls ? 'Walls connected: ' + walls.value : '',
        'Timeline: ' + val('#quote-timeline'),
        'Notes: ' + val('#quote-notes'),
        '---', 'Product URL: ' + productUrl
      ];
    } else {
      // Colorway comes from the modal's own select, not the page — the whole
      // point is that they can change it here.
      var chosenColorway = val('[data-colorway-select]') || sel.colorway;
      var chosenMaterials = checkedIn('[data-material-choice]:checked') || sel.material;
      body = [
        'CUSTOM VERSION REQUEST', '---',
        'From: ' + val('#custom-customer-name'),
        'Email: ' + val('#custom-customer-email'),
        val('#custom-customer-phone') ? 'Phone: ' + val('#custom-customer-phone') : '',
        val('#custom-customer-company') ? 'Company: ' + val('#custom-customer-company') : '',
        val('#custom-customer-tags') ? 'Account type: ' + val('#custom-customer-tags') : '',
        '---',
        'Pattern: ' + productTitle,
        'Base colorway: ' + chosenColorway,
        sel.colorway && chosenColorway !== sel.colorway
          ? 'Was viewing: ' + sel.colorway : '',
        'Materials wanted: ' + chosenMaterials,
        'Changes requested: ' + checkedIn('[data-change-type]:checked'),
        'Description: ' + val('#custom-description'),
        'Colorway direction: ' + val('#custom-colorway-direction'),
        'Project Name: ' + val('#custom-project-name'),
        'Sq Ft: ' + val('#custom-sqft'),
        'Timeline: ' + val('#custom-timeline'),
        'Notes: ' + val('#custom-notes'),
        '---', 'Product URL: ' + productUrl
      ];
    }

    form.querySelector('[name="contact[subject]"]').value =
      type === 'quote' ? 'Quote Request: ' + productTitle
        : type === 'availability' ? 'Availability Inquiry — ' + productTitle
        : 'Custom Request: ' + productTitle;
    form.querySelector('[name="contact[body]"]').value = body.filter(Boolean).join('\n');
    // Let Shopify's real contact form handle delivery, server errors and its
    // spam challenge. A fetched challenge/error page is not a sent request.
    return true;
  }

  var ACTIONS = {
    'request-quote': 'ws-modal-quote',
    'request-custom': 'ws-modal-custom',
    'open-material-guide': 'ws-modal-material',
    'open-materials-modal': 'ws-modal-material',
    'open-availability': 'ws-modal-availability'
  };

  document.addEventListener('click', function (event) {
    var closer = event.target.closest('[data-modal-close]');
    if (closer) {
      closeModal(closer.closest('.ws-modal'));
      return;
    }

    var btn = event.target.closest('[data-action]');
    if (!btn) return;
    var action = btn.dataset.action;

    if (ACTIONS[action]) {
      // The trigger may be a real link with a working href. Only swallow the
      // navigation once we know the modal exists to replace it with.
      if (document.getElementById(ACTIONS[action])) event.preventDefault();
      openModal(ACTIONS[action]);
      return;
    }

    // Submit buttons use the form submit event, including keyboard submission.
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('form[data-ws-request]')) return;
    if (!prepareSubmission(form)) event.preventDefault();
  });

  function restoreFormResult() {
    // Shopify renders this result after its own server-side processing. The
    // form anchor identifies which of the shared contact dialogs was sent.
    var form = document.getElementById(window.location.hash.slice(1));
    if (!form || !form.matches('form[data-ws-request]')) return;
    var result = form.querySelector('[data-ws-contact-result]');
    if (!result || !result.dataset.wsContactResult) return;
    var modal = form.closest('.ws-modal');
    openModal(modal.id);
    if (result.dataset.wsContactResult === 'success') {
      form.querySelector('.ws-modal__body').hidden = true;
      form.querySelector('.ws-modal__footer').hidden = true;
      form.querySelector('.ws-modal__success').classList.add('is-visible');
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreFormResult);
  else restoreFormResult();

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllModals();
  });

  window.WSModals = { open: openModal, closeAll: closeAllModals };
})();
